import { useCallback, useRef, useEffect } from "react";
import { ScrollView, TextInput } from "react-native";
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller";
import Animated, { useAnimatedStyle } from "react-native-reanimated";
import { ScreenContainer, FormInput, Button } from "@src/components";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { router } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useResetPassword } from "@src/hooks/useResetPassword";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { BackRoundedButton } from "@src/components/BackRoundedButton";
import { Icon } from "@src/components/Icon";

const KEYBOARD_HEIGHT = -380;
const KEYBOARD_OFFSET = KEYBOARD_HEIGHT / 2;

export default function ForgotPasswordScreen() {
  const { theme } = useUnistyles();
  const { progress } = useReanimatedKeyboardAnimation();
  const { top: topSafeArea } = useSafeAreaInsets();
  const { email, setEmail, isLoading, error, handleResetPassword, handleSubmitEditing } = useResetPassword();
  const inputRef = useRef<TextInput>(null);

  const handleDismiss = useCallback(() => {
    router.back();
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: progress.value * KEYBOARD_OFFSET }],
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer edges={["bottom"]} style={{ backgroundColor: "transparent" }}>
          {/* Close button */}
          <View style={[styles.backButtonContainer, { top: topSafeArea }]}>
            <BackRoundedButton onPress={handleDismiss} />
          </View>

          <ScrollView
            contentContainerStyle={{ flexGrow: 1, paddingHorizontal: theme.spacing["5xl"], justifyContent: "center" }}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View style={animatedStyle}>
              <View mb="6xl">
                <Text textStyle="h2" textAlign="center" style={styles.text}>
                  Reset password
                </Text>
              </View>
              <View mb="6xl">
                <Text textStyle="md/regular" textAlign="center" style={styles.text}>
                  Please provide your registered email address and we&apos;ll send you a link to reset your password.
                </Text>
              </View>

              <View mb="6xl">
                <FormInput
                  ref={inputRef}
                  variant="outline"
                  size="large"
                  value={email}
                  onChangeText={setEmail}
                  placeholder="Email"
                  autoCapitalize="none"
                  keyboardType="email-address"
                  returnKeyType="done"
                  onSubmitEditing={handleSubmitEditing}
                  iconRight={<Icon name="mail" size={20} color={theme.colors.white.default} />}
                />
              </View>

              {error && (
                <View mb="4xl">
                  <Text textStyle="sm/regular" textAlign="center" color="danger">
                    {error}
                  </Text>
                </View>
              )}

              <Button
                onPress={handleResetPassword}
                label="Reset Password"
                size="md"
                textStyle="md/regular"
                variant="primary"
                variantColor="blue"
                loading={isLoading}
                loadingText="Sending..."
                disabled={isLoading}
              />
            </Animated.View>
          </ScrollView>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  backButtonContainer: {
    position: "absolute",
    left: theme.spacing["3xl"],
    zIndex: 1,
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));
